import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App as AntdApp } from 'antd';

import ColumnHeader from './ColumnHeader';
import {
	MAX_FROZEN,
	columnsFromEditorRows,
	editorRowsFor,
	frozenCount,
	tableSortIsValid,
} from './cardTableModel';
import { putTableLayout } from '../../services/cardLayouts';
import useStableCallback from '../../hooks/useStableCallback';
import { clearColumnFilter, columnFilterOf, readColumnFilter, writeColumnFilter } from '../../utils/headerFilterModel';
import {
	ariaSortOf,
	canClearSort,
	columnSortOf,
	columnSortState,
	headerLabelOf,
	nextHeaderSort,
} from '../../utils/tableHeaderModel';

// Columns that are not data: the row's own Open · ⋯ and the manual order's grip.
const PLAIN_KEYS = new Set(['actions', 'sys:rank-handle']);

const FILTER_REASON = 'Use Filter in the toolbar for now.';
// Why a column's header filter is the builder's (KPR-1639), or why there is none.
const SET_IN_BUILDER = 'Set in the filter builder';
const OPENS_BUILDER = 'Opens the filter builder';
const TITLE_FILTER_REASON = 'Search finds items by their title.';
const GONE_FILTER_REASON = 'This field cannot be filtered here.';
const ONE_CARD_REASON = 'Choose one card to arrange its columns.';
const NO_COLLECTION_REASON = 'Open a collection’s list to arrange its columns.';
const SAVING_REASON = 'Saving the columns…';
// How long a saved change holds the menu while the new layout is read back.
const SETTLE_MS = 5000;

/**
 * The items page's column headers (KPR-1638, KPE-051 ruling i04): `columns`
 * (ItemsTable's, cell renders already memoised) with each data column's title
 * drawn as a `ColumnHeader` and its `th` carrying `aria-sort`. A layer on top:
 * every column keeps its `render` function, so a sort change redraws headers
 * and never a cell (KPR-1557).
 *
 * The order is level 1 of the address, through `headerMenu.sort`
 * (useListSort's `levelsProps`): a header writes it with `onChangeFirst` and
 * the Sort control in the toolbar, reading the same hook, shows the same field
 * and direction. The then-by levels are not touched.
 *
 * Hide and Freeze write the person's OWN table layout (scope 'user', never the
 * card's or the collection's), through the same rows the Columns… editor builds
 * (cardTableModel: `editorRowsFor`, `columnsFromEditorRows`), so the leading
 * frozen run, the ceiling of three and a repeating group's kept cell are the
 * editor's rules. What is stored starts from the columns on screen (the layout,
 * or an applied saved filter's own, which then stop standing in:
 * `headerMenu.onColumnsChanged`) and keeps the layout's default order. Another
 * change is refused while one is saving and until the new layout has been read
 * back, so two quick clicks cannot overwrite each other.
 *
 * A column's FILTER (KPR-1639, KPE-051 ruling i06) is a condition in the
 * page's one `?q=` (`headerMenu.filter`: the query, the filter vocabulary and
 * the setter, plus how to open the builder). The header reads its column's
 * top-level condition and writes it through utils/headerFilterModel.js; where
 * it cannot show the condition, or does not filter that type (a time of day), it opens
 * the builder instead and writes nothing. Every write is from the query in
 * the address at that moment, so a chip or the builder changing it under an
 * open panel is never undone.
 *
 *   headerMenu = { sort: levelsProps, onColumnsChanged, filter: { query, fields, onChange, openBuilder } }
 *
 * Without `headerMenu` the columns come back as they went in.
 */
export default function useTableHeaders({
	columns,
	headerMenu = null,
	cardId = null,
	cardDefs,
	defsByName,
	// The columns on screen before the attachments gate: what a write starts from.
	layoutColumns,
	// The layout's body, for its default order.
	layoutBody = null,
	collectionId = null,
	crossCollection = false,
	isMobile = false,
}) {
	const { message } = AntdApp.useApp();
	const sortProps = headerMenu?.sort || null;
	const field = sortProps?.field;
	const direction = sortProps?.direction;
	const source = sortProps?.source || null;
	const sort = useMemo(() => ({ field, direction, source }), [field, direction, source]);
	const options = sortProps?.options;
	const offered = useMemo(() => (options ? new Set(options.map(option => option.value)) : null), [options]);
	const onChangeFirst = useStableCallback(sortProps?.onChangeFirst);
	const onColumnsChanged = useStableCallback(headerMenu?.onColumnsChanged);
	// ---- the filter: the address's `?q=`, read and written whole ----
	const filterProps = headerMenu?.filter || null;
	const query = filterProps?.query || '';
	const filterFields = filterProps?.fields;
	const hasFilter = Boolean(filterProps);
	const queryRef = useRef(query);
	queryRef.current = query;
	const onQueryChange = useStableCallback(filterProps?.onChange);
	const openBuilder = useStableCallback(filterProps?.openBuilder);
	const onFilter = useStableCallback((key, action, draft) => {
		if (action === 'builder') { if (typeof openBuilder === 'function') openBuilder(); return; }
		const column = columnFilterOf(key, { defsByName, fields: filterFields });
		if (!column || typeof onQueryChange !== 'function') return;
		const current = queryRef.current || undefined;
		const next = action === 'clear' ? clearColumnFilter(current, column) : writeColumnFilter(current, column, draft);
		if ((next || '') !== (current || '')) onQueryChange(next);
	});

	// ---- the layout the columns come from ----
	const rows = useMemo(
		() => (cardId && layoutColumns ? editorRowsFor(layoutColumns, cardDefs) : []),
		[cardId, layoutColumns, cardDefs]
	);
	const rowsByKey = useMemo(() => new Map(rows.map(row => [row.key, row])), [rows]);
	const frozen = frozenCount(rows);
	const layoutKey = useMemo(() => JSON.stringify(layoutColumns || []), [layoutColumns]);

	// ---- a write in flight, and the moment after it ----
	const [saving, setSaving] = useState(false);
	const [heldAt, setHeldAt] = useState(null);
	const savingRef = useRef(false);
	const mounted = useRef(true);
	useEffect(() => () => { mounted.current = false; }, []);
	useEffect(() => {
		if (heldAt === null) return undefined;
		const timer = setTimeout(() => setHeldAt(null), SETTLE_MS);
		return () => clearTimeout(timer);
	}, [heldAt]);
	// Busy while saving, and until the layout on screen is not the one saved from.
	const busy = saving || heldAt === layoutKey;

	const onSort = useStableCallback((key, mode) => {
		const { field: sortField } = columnSortOf(key, { defsByName, offered });
		if (!sortField || typeof onChangeFirst !== 'function') return;
		if (mode === 'asc' || mode === 'desc') onChangeFirst(sortField, mode);
		else if (mode === 'clear') onChangeFirst(null);
		else {
			const next = nextHeaderSort(sortField, sort);
			onChangeFirst(next.field, next.direction);
		}
	});

	const onArrange = useStableCallback(async (key, change) => {
		const row = rowsByKey.get(key);
		if (savingRef.current || busy || !row || !cardId || !collectionId) return;
		if (change === 'freeze' && (row.frozen || frozen >= MAX_FROZEN)) return;
		if (change === 'hide' && row.column.system === 'title') return;
		const nextRows = rows.map(candidate => (candidate.key !== key ? candidate : (
			change === 'hide' ? { ...candidate, shown: false, frozen: false } : { ...candidate, frozen: change === 'freeze' }
		)));
		const kept = layoutBody?.sort;
		const body = {
			columns: columnsFromEditorRows(nextRows),
			...(kept && tableSortIsValid(kept, cardDefs) ? { sort: { field: kept.field, direction: kept.direction || 'desc' } } : {}),
		};
		const label = headerLabelOf(columns.find(column => column.key === key)?.title) || 'The column';
		savingRef.current = true;
		setSaving(true);
		try {
			// The person's own tier, whoever they are: a header change is theirs.
			await putTableLayout(cardId, { scope: 'user', collectionId, body });
			if (mounted.current) setHeldAt(layoutKey);
			message.success(change === 'hide' ? `Hid ${label}. Columns… brings it back.` : change === 'freeze' ? `Froze ${label}.` : `Unfroze ${label}.`);
			onColumnsChanged?.();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Saving the columns failed.');
		} finally {
			savingRef.current = false;
			if (mounted.current) setSaving(false);
		}
	});

	const enabled = Boolean(headerMenu);
	return useMemo(() => {
		if (!enabled) return columns;
		const arrangeFor = (key) => {
			if (key === 'title') return null;
			const reason = !cardId ? ONE_CARD_REASON : (!collectionId || crossCollection ? NO_COLLECTION_REASON : null);
			const row = rowsByKey.get(key);
			if (!reason && !row) return null;
			if (reason) return { hide: { reason }, freeze: isMobile ? null : { frozen: false, reason } };
			return {
				hide: { reason: busy ? SAVING_REASON : null },
				// Freezing is for a desktop: a phone's title already takes 44% of the width.
				freeze: isMobile ? null : {
					frozen: row.frozen,
					reason: busy ? SAVING_REASON : (!row.frozen && frozen >= MAX_FROZEN ? `At most ${MAX_FROZEN} columns stay frozen, the title included.` : null),
				},
			};
		};
		const filterFor = (key) => {
			if (!hasFilter) return { mode: 'off', reason: FILTER_REASON };
			const target = columnFilterOf(key, { defsByName, fields: filterFields });
			if (!target) return { mode: 'off', reason: key === 'title' || key === 'sys:title' ? TITLE_FILTER_REASON : FILTER_REASON };
			if (!target.field) return { mode: 'off', reason: GONE_FILTER_REASON };
			const read = readColumnFilter(query, target);
			// The item picker reads this collection's records: without one, the builder's title box is the way.
			const kind = target.kind === 'lookup' && !collectionId ? null : target.kind;
			if (kind && read.status !== 'builder') {
				return { mode: 'header', reason: null, active: read.active, column: { ...target, collectionId }, read: { ...read, query } };
			}
			return { mode: 'builder', reason: kind ? SET_IN_BUILDER : OPENS_BUILDER, active: read.active };
		};
		return columns.map((column) => {
			const key = column.key;
			if (PLAIN_KEYS.has(key) || typeof key !== 'string') return column;
			const label = headerLabelOf(column.title);
			const { field: sortField, reason } = columnSortOf(key, { defsByName, offered });
			const state = columnSortState(sortField, sort);
			const aria = ariaSortOf(sortField, sort);
			const model = {
				label,
				sort: {
					field: sortField,
					reason,
					state,
					canClear: canClearSort(sortField, sort),
					willClear: Boolean(sortField) && nextHeaderSort(sortField, sort).field === null,
				},
				filter: filterFor(key),
				arrange: arrangeFor(key),
			};
			return {
				...column,
				title: (
					<ColumnHeader columnKey={key} model={model} align={column.align} isMobile={isMobile} onSort={onSort} onArrange={onArrange} onFilter={onFilter}>
						{column.title}
					</ColumnHeader>
				),
				onHeaderCell: (...args) => ({
					...(column.onHeaderCell ? column.onHeaderCell(...args) : null),
					...(aria ? { 'aria-sort': aria } : {}),
				}),
			};
		});
	}, [columns, enabled, cardId, collectionId, crossCollection, rowsByKey, frozen, isMobile, busy, defsByName, offered, sort, onSort, onArrange, onFilter, hasFilter, filterFields, query]);
}
